import React, { useMemo, useRef, useState } from 'react'
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Textarea,
  Switch,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Link,
  Code,
  Divider,
  Collapse,
  useDisclosure as useCollapseDisclosure,
} from '@chakra-ui/react'
import { FiSettings, FiAlertTriangle, FiExternalLink, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { getExplorerAddressUrl, getExplorerTxUrl } from '@/constants/chains'
import {
  contractService,
  type LifecycleBatchTriggerResult,
} from '@/services/admin/contractService'
import { LifecycleBatchItemResults } from './LifecycleBatchItemResults'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import {
  getTriggerProgressStatus,
  ProgressModal,
  type ProgressStatus,
} from '@/components/common/ProgressModal'
import {
  findEntriesWithoutPrefix,
  parseMultilineEntries,
} from '@/components/common/triggerInputHelpers'
import { useApiError } from '@/hooks/useApiError'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import { buildContractChainOptions } from './contractChainOptions'

// Initialization result interface
interface InitializationResult extends LifecycleBatchTriggerResult {
  details?: {
    successful_initializations?: Array<{
      order_id: string
      contract_address: string
      chain_id: number
      initialization_status: string
      initialization_tx_hash?: string
    }>
    failed_initializations?: Array<{
      order_id: string
      error: string
      details?: any
    }>
  }
}

export const ContractInitializationTrigger: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleError } = useApiError()
  const { currentEnv } = useNetworkEnvironment()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const warningBg = useColorModeValue('orange.50', 'orange.900')
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure()
  const { isOpen: isProgressOpen, onOpen: onProgressOpen, onClose: onProgressClose } = useDisclosure()

  const [orderIds, setOrderIds] = useState('')
  const [chainId, setChainId] = useState('')
  const [limit, setLimit] = useState(10)
  const [forceInitialization, setForceInitialization] = useState(false)
  const [orderIdsError, setOrderIdsError] = useState('')
  const [results, setResults] = useState<InitializationResult | null>(null)
  const { isOpen: isDetailsOpen, onToggle: onToggleDetails } = useCollapseDisclosure()
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>('idle')
  const [progressCurrent, setProgressCurrent] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const chainOptions = useMemo(() => buildContractChainOptions(currentEnv), [currentEnv])
  const chainOptionMap = useMemo(
    () => new Map(chainOptions.map(option => [option.value, option])),
    [chainOptions]
  )
  const chainLabelByNumericId = useMemo(
    () => new Map(chainOptions.map(option => [option.chainId, option.label])),
    [chainOptions]
  )
  const selectedChainId = chainId
    ? chainOptionMap.get(chainId)?.chainId ?? 'unknown'
    : null

  const executeInitialization = async () => {
    // Parse and validate order IDs
    const orderIdList = parseMultilineEntries(orderIds)

    const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
    if (invalidIds.length > 0) {
      throw new Error(`Invalid order IDs: ${invalidIds.join(', ')}`)
    }

    const params: any = {
      limit,
      force: forceInitialization
    }

    if (orderIdList.length > 0) {
      params.orderIds = orderIdList
    }

    if (chainId) {
      params.chainId = chainId
    }

    setProgressTotal(limit)
    setProgressCurrent(0)
    setProgressMessage(t('monitoring.contractInitialization.progress.preparing'))
    setProgressStatus('processing')
    onProgressOpen()

    return await contractService.triggerContractInitialization(params, currentEnv)
  }

  const mutation = useMutation({
    mutationFn: executeInitialization,
    onSuccess: (response) => {
      if (response) {
        setResults(response)
        const { queued, alreadyQueued, skipped, failed, total } = response
        setProgressTotal(total)
        setProgressCurrent(total)
        setProgressStatus(getTriggerProgressStatus(failed))
        setProgressMessage(t('monitoring.contractInitialization.progress.completed'))
        
        // Add to operation history if available
        if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
          (window as any).addOperationHistory({
            type: 'contractInitialization',
            operator: 'Admin',
            result: failed > 0 ? 'partial' : 'success',
            details: `Queued: ${queued}, Already Queued: ${alreadyQueued}, Skipped: ${skipped}, Failed: ${failed}`,
            metadata: { queued, alreadyQueued, skipped, failed }
          })
        }
        
        const toastMethod = failed > 0
          ? toast.warning
          : queued > 0
          ? toast.success
          : toast.info
        
        toastMethod({
          title: t('monitoring.contractInitialization.success.title'),
          description: t('monitoring.contractInitialization.success.description', {
            queued: queued ?? 0,
            alreadyQueued: alreadyQueued ?? 0,
            skipped: skipped ?? 0,
            failed: failed ?? 0
          }),
          duration: 5000,
          isClosable: true,
          id: 'contract-initialization-success',
        })
      }
    },
    onError: (error: any) => {
      setProgressStatus('failed')
      const errorMessage = error.response?.data?.error || error.message || 'Failed to trigger contract initialization'
      setProgressMessage(errorMessage)
      
      // Add to operation history if available
      if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
        (window as any).addOperationHistory({
          type: 'contractInitialization',
          operator: 'Admin',
          result: 'failed',
          details: errorMessage,
          errorCode: error.response?.status
        })
      }
      
      handleError(error, 'Contract initialization')
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Contract Initialization Error Details:', {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        })
      }
    },
  })

  const handleOrderIdsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setOrderIds(value)
    
    if (value) {
      const orderIdList = parseMultilineEntries(value)
      const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
      if (invalidIds.length > 0) {
        setOrderIdsError(`Invalid order IDs: ${invalidIds.slice(0, 3).join(', ')}`)
      } else {
        setOrderIdsError('')
      }
    } else {
      setOrderIdsError('')
    }
  }

  const handleSubmit = () => {
    if (orderIdsError) {
      return
    }
    
    // Check if force initialization is enabled and show confirmation dialog
    if (forceInitialization) {
      onConfirmOpen()
    } else {
      mutation.mutate()
    }
  }

  const handleConfirmInitialization = () => {
    onConfirmClose()
    mutation.mutate()
  }

  const getStatColor = (value: number, type: 'queued' | 'alreadyQueued' | 'skipped' | 'failed') => {
    if (value === 0) return 'gray'
    switch (type) {
      case 'queued':
        return 'green'
      case 'alreadyQueued':
        return 'blue'
      case 'skipped':
        return 'yellow'
      case 'failed':
        return 'red'
      default:
        return 'gray'
    }
  }

  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        {t('monitoring.contractInitialization.description')}
      </Alert>

      <Box>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={!!orderIdsError}>
            <FormLabel>{t('monitoring.contractInitialization.fields.orderIds.label')}</FormLabel>
            <Textarea
              placeholder={t('monitoring.contractInitialization.fields.orderIds.placeholder')}
              value={orderIds}
              onChange={handleOrderIdsChange}
              rows={4}
              isDisabled={mutation.isPending}
            />
            {!orderIdsError && (
              <FormHelperText>
                {t('monitoring.contractInitialization.fields.orderIds.helper')}
              </FormHelperText>
            )}
            <FormErrorMessage>{orderIdsError}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.contractInitialization.fields.chainId.label')}</FormLabel>
            <DropdownSelect
              placeholder={t('monitoring.contractInitialization.fields.chainId.placeholder')}
              emptyOptionLabel={t('monitoring.contractInitialization.fields.chainId.placeholder')}
              value={chainId}
              options={chainOptions}
              onChange={setChainId}
              ariaLabel={t('monitoring.contractInitialization.fields.chainId.label')}
              isDisabled={mutation.isPending}
            />
            <FormHelperText>
              {t('monitoring.contractInitialization.fields.chainId.helper')}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.contractInitialization.fields.limit.label')}</FormLabel>
            <NumberInput
              value={limit}
              onChange={(_, value) => setLimit(value || 10)}
              min={1}
              max={100}
              isDisabled={mutation.isPending}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              {t('monitoring.contractInitialization.fields.limit.helper')}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <HStack justify="space-between" align="center">
              <FormLabel htmlFor="force-initialization" mb="0">
                {t('monitoring.contractInitialization.fields.forceInitialization.label')}
              </FormLabel>
              <Switch
                id="force-initialization"
                isChecked={forceInitialization}
                onChange={(e) => setForceInitialization(e.target.checked)}
                isDisabled={mutation.isPending}
                colorScheme="orange"
              />
            </HStack>
            {forceInitialization && (
              <FormHelperText mt={2}>
                {t('monitoring.contractInitialization.fields.forceInitialization.helper')}
              </FormHelperText>
            )}
          </FormControl>
          {forceInitialization && (
            <Alert 
              status="warning" 
              borderRadius="md"
              bg={warningBg}
            >
              <AlertIcon as={FiAlertTriangle} />
              <Box>
                <AlertDescription>
                  {t('monitoring.contractInitialization.fields.forceInitialization.warning')}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          <Button
            colorScheme="blue"
            leftIcon={<FiSettings />}
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            loadingText={t('monitoring.contractInitialization.button.loading')}
            isDisabled={mutation.isPending || !!orderIdsError}
          >
            {t('monitoring.contractInitialization.button.submit')}
          </Button>
          
          {mutation.isPending && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Processing Initialization</AlertTitle>
                <AlertDescription>
                  <Text fontSize="sm">
                    This operation may take up to 30 seconds depending on the number of contracts.
                  </Text>
                  <Text fontSize="sm" mt={1}>
                    Each contract initialization will be queued for background processing.
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>
      </Box>

      {results && (
        <Box
          bg={bgColor}
          p={6}
          borderRadius="md"
          borderWidth={1}
          borderColor={borderColor}
        >
          <VStack spacing={4} align="stretch">
            <Box fontWeight="semibold" fontSize="lg">
              {t('monitoring.contractInitialization.results.title')}
            </Box>
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractInitialization.results.total')}
                </StatLabel>
                <StatNumber>{results.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractInitialization.results.queued')}</span>
                    <Badge colorScheme={getStatColor(results.queued, 'queued')} size="sm">
                      {results.queued > 0 ? 'Queued' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.queued, 'queued')}.500`}>
                  {results.queued}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.queued / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractInitialization.results.alreadyQueued')}</span>
                    <Badge colorScheme={getStatColor(results.alreadyQueued, 'alreadyQueued')} size="sm">
                      {results.alreadyQueued > 0 ? 'Already In Queue' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.alreadyQueued, 'alreadyQueued')}.500`}>
                  {results.alreadyQueued}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.alreadyQueued / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractInitialization.results.skipped')}</span>
                    <Badge colorScheme={getStatColor(results.skipped, 'skipped')} size="sm">
                      {results.skipped > 0 ? 'Skipped' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.skipped, 'skipped')}.500`}>
                  {results.skipped}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.skipped / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractInitialization.results.failed')}</span>
                    <Badge colorScheme={getStatColor(results.failed, 'failed')} size="sm">
                      {results.failed > 0 ? 'Failed' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.failed, 'failed')}.500`}>
                  {results.failed}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.failed / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
            </SimpleGrid>

            <LifecycleBatchItemResults items={results.items} />
            
            {/* Detailed Results Toggle */}
            {((results as any).details?.successful_initializations?.length > 0 || (results as any).details?.failed_initializations?.length > 0) && (
              <>
                <Divider />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleDetails}
                  leftIcon={isDetailsOpen ? <FiChevronDown /> : <FiChevronRight />}
                >
                  {isDetailsOpen ? 'Hide' : 'Show'} Initialization Details
                </Button>
                
                <Collapse in={isDetailsOpen}>
                  <VStack spacing={4} align="stretch">
                    
                    {/* Successful Initializations */}
                    {results.details?.successful_initializations && results.details.successful_initializations.length > 0 && (
                      <Box>
                        <Text fontWeight="medium" color="green.500" mb={2}>
                          Successful Initializations ({results.details.successful_initializations.length})
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {results.details.successful_initializations.map((initialization, index) => {
                            const explorerUrl = initialization.initialization_tx_hash 
                              ? getExplorerTxUrl(
                                  initialization.chain_id,
                                  initialization.initialization_tx_hash
                                )
                              : getExplorerAddressUrl(
                                  initialization.chain_id,
                                  initialization.contract_address
                                )
                            const chainLabel = chainLabelByNumericId.get(initialization.chain_id)
                            return (
                              <Box key={index} p={3} borderWidth={1} borderColor="green.200" borderRadius="md" bg="green.50">
                                <HStack justify="space-between" wrap="wrap">
                                  <VStack align="start" spacing={1} fontSize="sm">
                                    <HStack>
                                      <Badge variant="outline">{chainLabel || `Chain ${initialization.chain_id}`}</Badge>
                                      <Code fontSize="xs">{initialization.order_id}</Code>
                                    </HStack>
                                    <Text color="gray.600">
                                      Status: {initialization.initialization_status}
                                    </Text>
                                  </VStack>
                                  {explorerUrl && (
                                    <Link href={explorerUrl} isExternal color="blue.500" fontSize="sm">
                                      {initialization.initialization_tx_hash ? 'View Transaction' : 'View Address'} <FiExternalLink style={{ display: 'inline' }} />
                                    </Link>
                                  )}
                                </HStack>
                              </Box>
                            )
                          })}
                        </VStack>
                      </Box>
                    )}
                    
                    {/* Failed Initializations */}
                    {results.details?.failed_initializations && results.details.failed_initializations.length > 0 && (
                      <Box>
                        <Text fontWeight="medium" color="red.500" mb={2}>
                          Failed Initializations ({results.details.failed_initializations.length})
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {results.details.failed_initializations.map((failure, index) => (
                            <Box key={index} p={3} borderWidth={1} borderColor="red.200" borderRadius="md" bg="red.50">
                              <VStack align="stretch" spacing={2} fontSize="sm">
                                <HStack>
                                  <Code fontSize="xs">{failure.order_id}</Code>
                                </HStack>
                                <Text color="red.600" fontWeight="medium">
                                  {failure.error}
                                </Text>
                              </VStack>
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Collapse>
              </>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <>
                <Divider />
                <Text fontSize="sm" color="gray.500">
                  Debug: {selectedChainId && `Chain: ${selectedChainId}`} | 
                  {orderIds ? `${orderIds.split('\n').filter(id => id.trim()).length} specific IDs` : 'All contracts'} | 
                  Limit: {limit}
                </Text>
              </>
            )}
          </VStack>
        </Box>
      )}

      {/* Force Initialization Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('confirmDialog.forceInitialization.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('confirmDialog.forceInitialization.message')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>
                {t('confirmDialog.forceInitialization.cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleConfirmInitialization} ml={3}>
                {t('confirmDialog.forceInitialization.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Progress Modal */}
      <ProgressModal
        isOpen={isProgressOpen}
        onClose={onProgressClose}
        title={t('monitoring.contractInitialization.progress.title')}
        total={progressTotal}
        current={progressCurrent}
        status={progressStatus}
        message={progressMessage}
        results={results ? {
          queued: results.queued,
          alreadyQueued: results.alreadyQueued,
          failed: results.failed,
          skipped: results.skipped
        } : undefined}
      />
    </VStack>
  )
}
